/**
 * Glossary Page
 * Comprehensive definitions of waste management and CalGreen compliance terms
 */

import { useState, useMemo, type JSX } from 'react';
import { Link } from 'react-router-dom';

interface GlossaryTerm {
  term: string;
  definition: string | JSX.Element;
  category: 'regulatory' | 'material' | 'process' | 'facility' | 'measurement' | 'compliance';
  crossReferences?: string[];
}

const glossaryTerms: GlossaryTerm[] = [
  // REGULATORY TERMS
  {
    term: 'AB 939',
    category: 'regulatory',
    definition:
      'The California Integrated Waste Management Act of 1989 (Assembly Bill 939), which established California\'s waste reduction goals and created the framework for statewide waste management. This law requires cities and counties to divert 50% of waste from landfills and created CalRecycle.',
  },
  {
    term: 'CalGreen',
    category: 'regulatory',
    definition:
      'The California Green Building Standards Code (Title 24, Part 11), which sets mandatory and voluntary green building standards for all new construction in California. Section 5.408 specifically addresses construction waste management and diversion requirements.',
    crossReferences: ['Section 5.408'],
  },
  {
    term: 'CalRecycle',
    category: 'regulatory',
    definition:
      'The California Department of Resources Recycling and Recovery, the state agency responsible for waste management policy, recycling programs, and compliance with California waste laws. CalRecycle maintains the Solid Waste Information System (SWIS) database.',
    crossReferences: ['SWIS'],
  },
  {
    term: 'Section 5.408',
    category: 'regulatory',
    definition:
      'The specific section of CalGreen that requires construction projects to divert at least 65% of construction and demolition debris from landfills. This is the primary regulation governing construction waste management in California.',
    crossReferences: ['CalGreen', 'Diversion Rate'],
  },
  {
    term: 'Title 24',
    category: 'regulatory',
    definition:
      'The California Building Standards Code, a collection of regulations governing building construction in California. Part 11 of Title 24 is the CalGreen code. Construction professionals must comply with all applicable Title 24 requirements.',
    crossReferences: ['CalGreen'],
  },

  // MATERIAL TERMS
  {
    term: 'C&D Debris',
    category: 'material',
    definition:
      'Construction and Demolition debris refers to materials generated from construction, renovation, demolition, or deconstruction activities. This includes wood, metal, concrete, drywall, roofing, and other building materials. Proper management of C&D debris is essential for meeting diversion requirements.',
    crossReferences: ['Waste Stream'],
  },
  {
    term: 'Ferrous Metal',
    category: 'material',
    definition:
      'Iron-based metals including steel, cast iron, and wrought iron. Ferrous metals are magnetic, highly recyclable, and typically command good value at recycling facilities. They are often among the easiest materials to divert from landfills.',
    crossReferences: ['Non-Ferrous Metal', 'MRF'],
  },
  {
    term: 'Non-Ferrous Metal',
    category: 'material',
    definition:
      'Metals that do not contain significant amounts of iron, including aluminum, copper, brass, and stainless steel. These metals are typically more valuable than ferrous metals and should be separated for maximum recycling value.',
    crossReferences: ['Ferrous Metal'],
  },
  {
    term: 'Gypsum Board',
    category: 'material',
    definition:
      'Also known as drywall or wallboard, gypsum board is a common interior wall material. Clean, unpainted gypsum can be recycled into new drywall or used as a soil amendment. Contaminated or painted gypsum is more difficult to recycle.',
  },
  {
    term: 'MSW',
    category: 'material',
    definition:
      'Municipal Solid Waste, or everyday garbage and refuse generated by households and businesses. MSW is different from C&D debris. Construction projects should minimize MSW generation and focus on separating C&D materials for diversion.',
    crossReferences: ['C&D Debris'],
  },
  {
    term: 'Aggregate',
    category: 'material',
    definition:
      'Crushed stone, gravel, or recycled concrete used as a base material in construction. Concrete and asphalt from demolition can be crushed and reused as aggregate, providing an excellent diversion opportunity.',
    crossReferences: ['Recycling'],
  },
  {
    term: 'Clean Fill',
    category: 'material',
    definition:
      'Uncontaminated soil, rock, sand, or gravel used for grading and filling. Clean fill can be reused on-site or at other projects and counts toward diversion goals. It must be free of chemicals, debris, and organic materials.',
    crossReferences: ['On-Site Reuse'],
  },
  {
    term: 'Green Waste',
    category: 'material',
    definition:
      'Organic plant materials such as grass clippings, tree trimmings, leaves, and brush from land clearing or landscaping. Green waste can be composted or converted to mulch and typically has established diversion pathways.',
    crossReferences: ['Composting'],
  },
  {
    term: 'Asphalt Pavement',
    category: 'material',
    definition:
      'Paving material made from petroleum-based asphalt binder and aggregate. Asphalt is 100% recyclable and can be reprocessed into new pavement. Reclaimed asphalt pavement (RAP) has high value and ready markets.',
  },

  // PROCESS TERMS
  {
    term: 'Diversion',
    category: 'process',
    definition:
      'The act of redirecting materials away from landfills through recycling, reusing, composting, or other recovery methods. Diversion is measured by weight and reported as a percentage. Meeting California\'s 65% diversion requirement is the primary compliance goal.',
    crossReferences: ['Diversion Rate', 'Section 5.408'],
  },
  {
    term: 'Source Separation',
    category: 'process',
    definition:
      'The practice of separating different waste materials at the point of generation (on the construction site) rather than after collection. Source separation improves material quality, increases recycling rates, and reduces contamination.',
    crossReferences: ['Commingled Waste'],
  },
  {
    term: 'Salvage',
    category: 'process',
    definition:
      'The recovery of materials from a demolition or renovation for reuse or resale. Salvaged items like doors, windows, fixtures, and cabinetry can be donated or sold rather than disposed. Salvage is an effective diversion method.',
    crossReferences: ['Deconstruction', 'Reuse'],
  },
  {
    term: 'Deconstruction',
    category: 'process',
    definition:
      'The systematic disassembly of a building to recover materials for reuse, rather than demolishing it. Deconstruction maximizes material recovery and diversion but typically takes longer and costs more than traditional demolition.',
    crossReferences: ['Salvage', 'Demolition'],
  },
  {
    term: 'Demolition',
    category: 'process',
    definition:
      'The tearing down or destruction of a building or structure. Traditional demolition is faster and cheaper than deconstruction but results in mixed debris that is harder to divert. Selective demolition balances speed with material recovery.',
    crossReferences: ['Deconstruction'],
  },
  {
    term: 'Recycling',
    category: 'process',
    definition:
      'Processing waste materials into new products. Recycling requires materials to be collected, sorted, cleaned, and remanufactured. Common C&D materials like metal, concrete, and wood are highly recyclable when properly separated.',
    crossReferences: ['MRF', 'Diversion'],
  },
  {
    term: 'Reuse',
    category: 'process',
    definition:
      'Using a material or product again in its existing form without reprocessing. Reuse includes on-site applications (using excavated soil for landscaping) or salvaging items for use elsewhere. Reuse is preferable to recycling when feasible.',
    crossReferences: ['On-Site Reuse', 'Salvage'],
  },
  {
    term: 'On-Site Reuse',
    category: 'process',
    definition:
      'Using materials directly on the project site without hauling them away. Examples include crushing concrete for base material, using excavated soil for grading, or chipping wood for mulch. On-site reuse reduces costs and counts toward diversion.',
    crossReferences: ['Reuse'],
  },
  {
    term: 'Composting',
    category: 'process',
    definition:
      'The biological decomposition of organic materials into a soil-like material called compost. Wood chips, clean wood, and green waste can be composted. Composting is an effective diversion method for organic C&D materials.',
    crossReferences: ['Green Waste'],
  },
  {
    term: 'Commingled Waste',
    category: 'process',
    definition:
      'Mixed waste materials that are collected together rather than separated by type. Commingled waste can be processed at a mixed waste processing facility but typically achieves lower diversion rates than source-separated materials.',
    crossReferences: ['Source Separation', 'MRF'],
  },

  // FACILITY TERMS
  {
    term: 'MRF',
    category: 'facility',
    definition:
      'Materials Recovery Facility, a specialized facility that receives, separates, and processes recyclable materials. MRFs use a combination of manual sorting and automated equipment to separate mixed materials. Some MRFs specialize in C&D debris.',
    crossReferences: ['Recycling', 'Transfer Station'],
  },
  {
    term: 'Transfer Station',
    category: 'facility',
    definition:
      'An intermediate facility where waste is unloaded from collection vehicles and reloaded onto larger trucks for transport to final disposal or processing facilities. Many transfer stations offer some recycling and sorting services.',
    crossReferences: ['Landfill'],
  },
  {
    term: 'Landfill',
    category: 'facility',
    definition:
      'A disposal site where waste is buried in the ground. Modern landfills are engineered with liners and leachate collection systems. The goal of CalGreen 5.408 is to minimize the amount of C&D debris sent to landfills.',
    crossReferences: ['Diversion', 'Section 5.408'],
  },
  {
    term: 'SWIS',
    category: 'facility',
    definition:
      'The Solid Waste Information System, CalRecycle\'s database of waste management facilities in California. SWIS provides information on recycling centers, transfer stations, landfills, and composting facilities. Use SWIS to find approved facilities near your project.',
    crossReferences: ['CalRecycle'],
  },
  {
    term: 'C&D Processing Facility',
    category: 'facility',
    definition:
      'A facility specifically designed to handle construction and demolition debris. These facilities sort, process, and recover materials from C&D waste, achieving higher diversion rates than general waste facilities.',
    crossReferences: ['C&D Debris', 'MRF'],
  },

  // MEASUREMENT TERMS
  {
    term: 'Diversion Rate',
    category: 'measurement',
    definition:
      'The percentage of waste diverted from landfills, calculated by weight. Diversion Rate = (Weight Diverted ÷ Total Waste Weight) × 100. CalGreen requires a minimum 65% diversion rate for most construction projects.',
    crossReferences: ['Diversion', 'Section 5.408'],
  },
  {
    term: 'Tipping Fee',
    category: 'measurement',
    definition:
      'The charge per ton for unloading waste at a disposal or processing facility. Tipping fees vary by facility type and location. Landfill tipping fees are often higher than recycling facility fees, providing economic incentive for diversion.',
  },
  {
    term: 'Weight Ticket',
    category: 'measurement',
    definition:
      'A document provided by waste facilities showing the weight of materials received. Weight tickets are essential compliance documentation, proving the quantity and destination of materials. Keep all weight tickets for your final report.',
    crossReferences: ['Documentation'],
  },
  {
    term: 'Waste Stream',
    category: 'measurement',
    definition:
      'The total flow of waste materials from source to final disposition. Construction projects typically have multiple waste streams (wood, metal, concrete) that can be managed separately. Understanding your waste streams is key to maximizing diversion.',
    crossReferences: ['C&D Debris'],
  },
  {
    term: 'Cubic Yard',
    category: 'measurement',
    definition:
      'A volume measurement commonly used for waste (3 feet × 3 feet × 3 feet). While haulers often measure in cubic yards, CalGreen compliance requires weight-based calculations. You\'ll need weight tickets from facilities, not just volume estimates.',
    crossReferences: ['Weight Ticket', 'Ton'],
  },
  {
    term: 'Ton',
    category: 'measurement',
    definition:
      'A weight measurement equal to 2,000 pounds (short ton). CalGreen diversion rates must be calculated by weight in tons, not volume. Always obtain weight tickets from facilities to document actual tonnage.',
    crossReferences: ['Weight Ticket', 'Diversion Rate'],
  },

  // COMPLIANCE TERMS
  {
    term: 'Certificate of Occupancy',
    category: 'compliance',
    definition:
      'A document issued by the local building department certifying that a building complies with building codes and is safe for occupancy. Many jurisdictions require proof of CalGreen 5.408 compliance before issuing the certificate of occupancy.',
    crossReferences: ['Jurisdiction'],
  },
  {
    term: 'County Approved',
    category: 'compliance',
    definition:
      'A service provider or facility that has been verified as meeting county-specific requirements for CalGreen compliance. County Approved status indicates that the provider has been confirmed as licensed, authorized, or franchised to operate in that jurisdiction and meets all local waste management standards. Verification typically includes confirming business licenses, franchise agreements, and compliance with county waste management authority requirements.',
    crossReferences: ['Pending Verification', 'Jurisdiction'],
  },
  {
    term: 'Pending Verification',
    category: 'compliance',
    definition:
      'A service provider or facility that operates in a county but whose CalGreen compliance status has not yet been confirmed with local authorities. Pending Verification status indicates the provider is known to serve the area but requires direct verification with the county building department or waste management authority to confirm their approval status. Always verify provider credentials with your local jurisdiction before engaging their services.',
    crossReferences: ['County Approved', 'Documentation'],
  },
  {
    term: 'Waste Management Plan',
    category: 'compliance',
    definition: (
      <span>
        A document detailing how a project will handle and divert construction waste. The plan identifies waste streams,
        diversion methods, and responsible parties. CalGreen requires a waste management plan for covered projects.
        Tools like <Link to="/" className="text-primary-600 hover:text-primary-700 underline">CalWMP</Link> simplify plan creation.
      </span>
    ),
    crossReferences: ['Section 5.408', 'Documentation'],
  },
  {
    term: 'Jurisdiction',
    category: 'compliance',
    definition:
      'The local government entity (city or county) with authority over building permits and code enforcement. Each jurisdiction may have additional waste diversion requirements beyond CalGreen. Always check with your local jurisdiction for specific rules.',
  },
  {
    term: 'Documentation',
    category: 'compliance',
    definition:
      'Records proving compliance with waste diversion requirements. Required documentation includes the waste management plan, weight tickets from facilities, hauler receipts, and final diversion calculations. Maintain complete documentation for inspections.',
    crossReferences: ['Weight Ticket', 'Waste Management Plan'],
  },
  {
    term: 'Building Permit',
    category: 'compliance',
    definition:
      'Official approval from the local jurisdiction to proceed with construction. Many jurisdictions require a waste management plan as part of the permit application or before final inspection. Non-compliance can delay permit approval or closure.',
    crossReferences: ['Jurisdiction', 'Certificate of Occupancy'],
  },
  {
    term: 'Final Report',
    category: 'compliance',
    definition:
      'Documentation submitted after project completion showing actual waste quantities, diversion methods, and final diversion rate. The final report includes weight tickets and calculations proving compliance with the 65% requirement.',
    crossReferences: ['Documentation', 'Diversion Rate'],
  },
];

function GlossaryCard({
  term,
  isExpanded,
  onClick,
}: {
  term: GlossaryTerm;
  isExpanded: boolean;
  onClick: () => void;
}) {
  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={onClick}
        className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-neutral-50 transition-colors text-left"
      >
        <span className="font-semibold text-neutral-900">{term.term}</span>
        <svg
          className={`w-5 h-5 text-neutral-500 transition-transform flex-shrink-0 ml-4 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200">
          <div className="text-neutral-700 leading-relaxed mb-3">{term.definition}</div>
          {term.crossReferences && term.crossReferences.length > 0 && (
            <div className="pt-3 border-t border-neutral-200">
              <span className="text-sm font-medium text-neutral-600">Related terms: </span>
              <span className="text-sm text-primary-600">
                {term.crossReferences.join(', ')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Glossary() {
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { value: 'all', label: 'All Terms' },
    { value: 'regulatory', label: 'Regulations' },
    { value: 'material', label: 'Materials' },
    { value: 'process', label: 'Processes' },
    { value: 'facility', label: 'Facilities' },
    { value: 'measurement', label: 'Measurements' },
    { value: 'compliance', label: 'Compliance' },
  ];

  const filteredTerms = useMemo(() => {
    let terms = glossaryTerms;

    // Filter by category
    if (selectedCategory !== 'all') {
      terms = terms.filter((term) => term.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      terms = terms.filter(
        (term) =>
          term.term.toLowerCase().includes(query) ||
          (typeof term.definition === 'string' && term.definition.toLowerCase().includes(query))
      );
    }

    // Sort alphabetically
    return terms.sort((a, b) => a.term.localeCompare(b.term));
  }, [selectedCategory, searchQuery]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
          ← Back to Home
        </Link>
      </div>

      <h1 className="text-4xl font-bold text-neutral-900 mb-4">Glossary</h1>

      <p className="text-lg text-neutral-600 mb-8">
        Comprehensive definitions of waste management and CalGreen compliance terms
      </p>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search terms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 pl-11 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <svg
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Category Filter */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => {
                setSelectedCategory(category.value);
                setExpandedTerm(null);
              }}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                selectedCategory === category.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4">
        <p className="text-sm text-neutral-600">
          Showing {filteredTerms.length} of {glossaryTerms.length} terms
        </p>
      </div>

      {/* Glossary Terms */}
      <div className="space-y-3 mb-12">
        {filteredTerms.length > 0 ? (
          filteredTerms.map((term) => (
            <GlossaryCard
              key={term.term}
              term={term}
              isExpanded={expandedTerm === term.term}
              onClick={() => setExpandedTerm(expandedTerm === term.term ? null : term.term)}
            />
          ))
        ) : (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-8 text-center">
            <p className="text-neutral-600">No terms found matching your search.</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Additional Resources */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Learn More</h3>
        <p className="text-blue-800 mb-4">
          Explore our comprehensive guides to understand how these terms apply to your project.
        </p>
        <div className="space-y-2">
          <Link
            to="/docs/calgreen-overview"
            className="block text-primary-600 hover:text-primary-700 font-medium"
          >
            → CalGreen 5.408 Overview
          </Link>
          <Link
            to="/docs/faq"
            className="block text-primary-600 hover:text-primary-700 font-medium"
          >
            → Frequently Asked Questions
          </Link>
          <a
            href="https://www.calrecycle.ca.gov/"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-primary-600 hover:text-primary-700 font-medium"
          >
            → CalRecycle Official Website
          </a>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-neutral-200">
        <p className="text-sm text-neutral-600">
          <strong>Note:</strong> Definitions are provided for educational purposes and are based on California
          regulations and industry practice. Requirements and terminology may vary by jurisdiction. Always verify
          definitions and requirements with your local building department and relevant regulatory agencies.
        </p>
      </div>
    </div>
  );
}